<template>
  <div class="datepicker-container">
    <!--Input Text-->
    <input
      type="text"
      class="datepicker-input"
      @click="openModal"
      :placeholder="inputProps.placeholder || placeholder"
      readonly
      :value="formattedDateString"
    />
    <div class="datepicker-icon">
      <slot>
        <span></span>
      </slot>
    </div>

    <!-- Custom Modal Overlay -->
    <div
      v-if="datePickerModal"
      class="modal-overlay"
      data-cy="date-picker-modal"
      @click="closeModal"
    >
      <div @click.stop class="modal-content">
        <!-- Close Button -->
        <button
          class="modal-close-button"
          @click="closeModal"
          aria-label="Close date picker"
        >
          <span>&times;</span>
        </button>
        <CustomDatePickerContentV2
          @updateDate="handleDateUpdate"
          @updateDateRange="handleDateRangeUpdate"
          :disabledDates="disabledDates"
          :previous-selected-date="date"
          :previous-end-date="endDate"
          :min-date="minDate"
          :pickup-month="pickupMonth"
          :pickup-year="pickupYear"
          :isOneWay="isOneWay"
          :isReturnDateField="isReturnDateField"
        />
      </div>
    </div>
  </div>
</template>

<script>
import CustomDatePickerContentV2 from "@/components/CustomDatePickerContentV2.vue";
export default {
  name: "CustomDatepickerV3",
  props: {
    value: {
      type: Date,
      default: null,
    },
    endValue: {
      type: Date,
      default: null,
    },
    placeholder: {
      type: String,
      default: "Select a date...",
    },
    locale: {
      type: String,
      default: "en-GB",
    },
    inputProps: Object,
    minDate: Date,
    pickupMonth: {
      type: Number,
      default: null,
    },
    pickupYear: {
      type: Number,
      default: null,
    },
    disabledDates: {
      type: Array,
      default: () => [
        {
          start: null,
          end: new Date(new Date().setDate(new Date().getDate() - 1)),
        },
      ],
    },
    isOneWay: {
      type: Boolean,
      default: false,
    },
    isReturnDateField: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    CustomDatePickerContentV2,
  },
  data() {
    return {
      date: this.value,
      endDate: this.endValue,
      datePickerModal: false,
    };
  },
  watch: {
    value(newVal) {
      this.date = newVal;
    },
    endValue(newVal) {
      this.endDate = newVal;
    },
  },
  methods: {
    openModal() {
      this.datePickerModal = true;
    },
    closeModal() {
      this.datePickerModal = false;
    },
    handleDateUpdate(newDate) {
      this.date = newDate;
      this.$emit("input", newDate);
      this.$emit("dateSelected", newDate.getMonth(), newDate.getFullYear());

      // For one-way flights, close modal immediately
      if (this.isOneWay) {
        this.closeModal();
      }
      // For two-way flights, keep modal open for the second date selection
    },
    handleDateRangeUpdate(dateRange) {
      this.date = dateRange.startDate;
      this.endDate = dateRange.endDate;

      // Emit both start and end date
      this.$emit("input", dateRange.startDate);

      // Emit the end date for use in the parent component
      if (this.endDate) {
        this.$emit("update:endValue", dateRange.endDate);
      }

      // Combined event for both dates
      this.$emit("dateRangeSelected", dateRange.startDate, dateRange.endDate);

      this.closeModal();
    },
    isValidDate(date) {
      // If no date is provided, it's "valid" in the sense that we don't need to set a default
      if (!date) return true;

      // Otherwise check it against the min date
      return date >= this.minDate;
    },
    padWithZero(num) {
      return num < 10 ? `0${num}` : num;
    },
  },
  computed: {
    formattedDate() {
      if (!this.date) return "";

      let days = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      let months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      let dateObj = new Date(this.date);

      let dayOfWeek = days[dateObj.getDay()];
      let dayOfMonth = dateObj.getDate();
      let month = months[dateObj.getMonth()];
      let year = dateObj.getFullYear();

      return {
        dayOfWeek,
        month,
        dayOfMonth,
        year,
      };
    },
    formattedDateString() {
      if (!this.date) return "";
      // Always show only one date regardless of endValue
      return `${this.padWithZero(this.date.getDate())}/${this.padWithZero(
        this.date.getMonth() + 1
      )}/${this.date.getFullYear()}`;
    },
  },
  mounted() {
    // Only adjust the date if it's explicitly set but invalid
    // Don't set a default date if none was passed in
    if (this.date && !this.isValidDate(this.date)) {
      this.date = new Date();
    }
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/settings.scss";
.datepicker-container {
  position: relative; // This is essential
  width: 100%;
}
.datepicker-input {
  appearance: none; // attempt to remove browser default styles
  -webkit-appearance: none;
  -moz-appearance: none;

  background-color: #f6f6f4 !important;
  height: 64px !important;
  border-radius: 8px !important;
  border: none !important;
  -webkit-box-shadow: none !important;
  box-shadow: none !important;
  font-size: 16px !important;
  width: 100% !important;
  display: flex;
  align-items: center;
  padding: 16px;
  justify-content: space-between;
  cursor: pointer;

  &:hover,
  &:focus {
    outline: none;
    box-shadow: none;
  }

  // Style the placeholder text
  &::placeholder,
  &::-webkit-input-placeholder,
  &::-moz-placeholder,
  &:-ms-input-placeholder {
    color: $light-grey;
  }
}

.datepicker-input ::v-deep input[type="text"] {
  background: none;
  border: none;
  padding: 0 !important;
  padding-right: 40px;
}

.datepicker-icon {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}

.datepicker-container:nth-of-type(1) {
  margin-right: 0.75em;
}

.datepicker-container:nth-of-type(1) .datepicker-icon {
  right: 32px;
  position: absolute;
}

/* Custom Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.3); /* semi-transparent background */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000; /* To ensure the modal is above other page content */
}

.modal-content {
  background: white;
  padding: 20px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  max-width: 600px; /* Increased to accommodate dual calendar */
  width: 100%; /* This ensures the content tries to occupy the full width up to max-width */
  display: flex; /* Flex will help ensure that child elements can expand to full width */
  flex-direction: column; /* This ensures that the content is arranged vertically */
  position: relative; /* For absolute positioning of close button */
}

.modal-close-button {
  display: none; /* Hide on desktop by default */
}

/* Mobile styles */
@media (max-width: 780px) {
  .datepicker-container:nth-of-type(1) {
    padding-right: 0;
  }
  .datepicker-container:nth-of-type(1) .datepicker-icon {
    right: 16px;
  }

  .modal-content {
    max-width: 90%;
    padding: 15px;
  }

  .modal-close-button {
    display: flex; 
    position: absolute;
    top: 0px;
    right: 5px;
    background: none;
    border: none;
    font-size: 28px;
    cursor: pointer;
    color: #666;
    width: 36px;
    height: 36px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.2s ease;
    z-index: 1001;

    &:hover {
      background-color: #f0f0f0;
      color: #333;
    }

    &:focus {
      outline: 2px solid #ac6411;
      outline-offset: 2px;
    }

    span {
      line-height: 1;
      font-weight: normal;
    }
  }
}
</style>
